import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Send } from "lucide-react";
import { api } from "@/lib/api";
import { t, HULL_LABELS } from "@/lib/i18n";

const empty = {
  prospect_name: "",
  business_name: "",
  phone: "",
  email: "",
  hull_id: "",
  notes: "",
  close_type: "refer_only",
};

export default function NewReferralForm({ hulls, lang, onCreated }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const errs = {};
    if (!form.prospect_name.trim()) errs.prospect_name = t(lang, "required");
    if (!form.hull_id) errs.hull_id = t(lang, "required");
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      const { data } = await api.post("/referrals", form);
      toast.success(t(lang, "referral_created"), {
        description: data.prospect_name,
      });
      setForm(empty);
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(t(lang, "referral_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-3"
      data-testid="new-referral-form-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl grid place-items-center border border-[#d4a843]/40 bg-[#d4a843]/10">
          <UserPlus className="w-5 h-5 text-[#f3c669]" />
        </div>
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "new_referral_title")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{t(lang, "new_referral_sub")}</p>
        </div>
      </div>

      <form onSubmit={submit} data-testid="new-referral-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t(lang, "prospect_name")} err={errors.prospect_name}>
          <input
            className="eigoo-input"
            data-testid="input-prospect-name"
            value={form.prospect_name}
            onChange={set("prospect_name")}
            placeholder="Nguyen Van A"
            required
          />
        </Field>

        <Field label={t(lang, "business_name")}>
          <input
            className="eigoo-input"
            data-testid="input-business-name"
            value={form.business_name}
            onChange={set("business_name")}
            placeholder="Acme Co."
          />
        </Field>

        <Field label={t(lang, "phone")}>
          <input
            className="eigoo-input"
            data-testid="input-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+84 ..."
          />
        </Field>

        <Field label={t(lang, "prospect_email")}>
          <input
            type="email"
            className="eigoo-input"
            data-testid="input-email"
            value={form.email}
            onChange={set("email")}
            placeholder="prospect@example.com"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label={t(lang, "hull_service")} err={errors.hull_id}>
            <select
              className="eigoo-input"
              data-testid="input-hull-id"
              value={form.hull_id}
              onChange={set("hull_id")}
              required
            >
              <option value="">{t(lang, "hull_service_ph")}</option>
              {(hulls || []).map((h) => (
                <option key={h.id} value={h.id}>
                  {HULL_LABELS[lang]?.[h.id] || h.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label={t(lang, "notes")}>
            <textarea
              rows={3}
              className="eigoo-input resize-none"
              data-testid="input-notes"
              value={form.notes}
              onChange={set("notes")}
              placeholder={t(lang, "notes_ph")}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t(lang, "close_type_label")}
          </span>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { id: "refer_only", label: t(lang, "close_refer_only") },
              { id: "refer_close", label: t(lang, "close_refer_close") },
            ].map((opt) => (
              <label
                key={opt.id}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                  form.close_type === opt.id
                    ? "border-[#d4a843]/60 bg-[#d4a843]/10 text-white"
                    : "border-[#1e3a61] bg-[#0f2038]/60 text-slate-300 hover:border-[#d4a843]/30"
                }`}
                data-testid={`close-type-${opt.id}`}
              >
                <input
                  type="radio"
                  name="close_type"
                  value={opt.id}
                  checked={form.close_type === opt.id}
                  onChange={set("close_type")}
                  className="accent-[#d4a843]"
                />
                {opt.label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{t(lang, "close_hint")}</p>
        </div>

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="gold-btn inline-flex items-center gap-2"
            data-testid="submit-referral-button"
          >
            <Send className="w-4 h-4" />
            {busy ? t(lang, "submitting") : t(lang, "submit_referral")}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, err, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-2">{children}</div>
      {err && <span className="text-[11px] text-[#fca5a5] mt-1 block">{err}</span>}
    </label>
  );
}
